import React from 'react';
import { Home } from 'lucide-react';
import EmployeeAcknowledgment from './EmployeeAcknowledgment';
import DocumentArchiveButton from './DocumentArchiveButton';
import PVSPageLayout from './PVSPageLayout';

const OnSiteRA = ({ onReturnHome }) => (
  <PVSPageLayout className="py-12 px-4">
    <div className="max-w-4xl mx-auto pvs-glass border border-pvs-accent/30 rounded-xl shadow-pvs-glow">
      <div className="border-b border-pvs-border p-6">
          <div className="flex justify-between items-center gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex-1 min-w-0">On Site RA</h1>
            <DocumentArchiveButton />
            <button
              className="group flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
              onClick={onReturnHome}
              title="Return to Homepage"
            >
              <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
            </button>
          </div>
      </div>
      <div className="p-8 md:p-10 lg:p-12">
        <h2 className="text-2xl font-bold text-center mb-6 text-white">Risk Assessment: On-Site LPG Vessel Operations</h2>
        <table className="w-full border-collapse mb-8" style={{borderColor: '#0437F2'}}>
          <tbody>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold w-1/3" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Document Title</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>On-Site LPG Vessel Operations – Risk Assessment for Employees</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Document Reference</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>PVS-ORA-001</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Revision</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>1.0</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Date of Issue</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>19/07/2025</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Prepared by</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>Pressure Vessel Services Limited</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Approved by</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>Board of Directors</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Review Period</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>Every 3 years</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Next Review Date</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>19/07/2028</td>
            </tr>
          </tbody>
        </table>
      <section className="mb-8">
        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">1. Introduction and Scope</h3>
        <p className="text-pvs-text-muted">
          This Risk Assessment is designed for use by Pressure Vessel Services Limited employees when conducting on-site operations involving LPG storage vessels. This includes, but is not limited to, lifting vessels with lorry-mounted cranes, installation, gas transfer/exchange, removal of liquid/vapour contents, and general vessel handling. This document aims to identify potential hazards and outline control measures to ensure compliance with the Health and Safety at Work Act 1974, the Lifting Operations and Lifting Equipment Regulations (LOLER), the Pressure Systems Safety Regulations 2000 (PSSR), and other relevant UK legislation.
        </p>
        <p className="text-pvs-text-muted mt-4 font-semibold">
          This document is a general risk assessment. Employees are responsible for conducting a visual inspection prior to commencing work for any additional dangers or unforeseen risks that may cause harm to themselves, colleagues, the general public, the vessel, or equipment. Risks are not limited to those listed below, and if it is the feeling of the employees that the particular job in question requires a further detailed, custom risk assessment, then it shall be done and approved by a Senior Engineer or Management.
        </p>
      </section>
      <section className="mb-8">
        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">2. General Information</h3>
        <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
          <li><strong>Company:</strong> Pressure Vessel Services Limited</li>
          <li><strong>Location:</strong> Client Sites (various locations).</li>
          <li><strong>Activity:</strong> On-site operations including lifting, installation, gas transfer/exchange, removal, and general handling of LPG bulk storage vessels.</li>
          <li><strong>Key Personnel:</strong> Gas Safe Engineers, qualified lifting personnel.</li>
          <li><strong>Equipment:</strong> Lorry-mounted cranes, Portable Compressor Units (PCU), gas transfer equipment, hand tools, PPE.</li>
          <li><strong>Reference Documents:</strong>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Written Scheme of Examination 2024</li>
              <li>PVS Health And Safety Policy 2025</li>
              <li>Work Instructions for Completion of Thorough Examinations of LPG Bulk Storage Vessels</li>
              <li>Method Statement: Lifting Operations and Transport of LPG Storage Vessels</li>
              <li>Method Statement: Vessel Examination and Preparation</li>
              <li>Method Statement: Control of Substances Hazardous to Health (COSHH)</li>
              <li>Safety Data Sheets (SDS) for all hazardous substances.</li>
              <li>Client site-specific health and safety policy guidelines.</li>
            </ul>
          </li>
        </ul>
      </section>
      <section className="mb-8">
        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">3. Responsibilities</h3>
        <p className="mb-2 text-pvs-text-muted"><strong>Overall Responsibility:</strong> The Board of Directors of the Employer.</p>
        <p className="mb-4 text-pvs-text-muted"><strong>Day-to-Day Responsibility:</strong> Health and Safety Officer (Senior Engineer on duty).</p>
        <p className="font-semibold mb-2 text-pvs-text-muted">Employee Responsibilities (On-Site):</p>
        <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
          <li>Take reasonable care for their own health and safety and that of others (colleagues, general public) who may be affected by their acts or omissions.</li>
          <li>Co-operate with the Health and Safety Officer and the Employer generally to enable compliance with health and safety duties and requirements.</li>
          <li>Comply with all health and safety instructions and rules, including those specific to client sites.</li>
          <li>Conduct a visual inspection of the site and surrounding areas prior to commencing work to identify any additional or unforeseen risks not covered in this assessment.</li>
          <li>Notify the on-duty Senior Engineer or management immediately of any unforeseen risks or hazards that require further assessment or a custom RAMS.</li>
          <li>Keep health and safety issues in the front of their minds and take personal responsibility for the health and safety implications of their own acts and omissions.</li>
          <li>Report all health and safety concerns, incidents, or near misses promptly to the Health and Safety Officer.</li>
          <li>Co-operate in the Employer's investigation of any incident or accident.</li>
          <li>Carry photographic ID at all times when attending a client site.</li>
          <li>Request a copy of the client site health and safety policy guidelines prior to attending site.</li>
        </ul>
      </section>
      <section className="mb-8">
        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">4. Hazard Identification and Risk Assessment (On-Site Operations)</h3>
        <div className="overflow-x-auto rounded-lg shadow-md">
          <table className="min-w-full">
            <thead>
              <tr className="bg-pvs-card-light">
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted">Hazard</th>
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted">Potential Harm</th>
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted">Existing Controls (Refer to relevant MS/WSE for detail)</th>
                <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted">Residual Risk</th>
              </tr>
            </thead>
            <tbody>
              {/* Lifting Operations (Lorry-Mounted Cranes) */}
              <tr>
                <td className="py-3 px-4 font-bold">Lifting Operations (Lorry-Mounted Crane)</td>
                <td className="py-3 px-4">Dropped load, crushing, striking, entanglement, structural failure of crane/vehicle, overturning.</td>
                <td className="py-3 px-4">
                  <ul>
                    <li>Only qualified personnel to operate crane.</li>
                    <li>Minimum of 2 people for all lifts.</li>
                    <li>Visual inspection of lifting equipment (chains/slings) prior to use.</li>
                    <li>Vessel lifted from designated points (chains for gas-free, slings for LPG-filled).</li>
                    <li>Person in charge conducts visual inspection of surrounding area (hazards, public, overhead lines).</li>
                    <li>Clear communication and signalling.</li>
                    <li>LOLER compliance (periodic testing/inspection of equipment).</li>
                    <li>Site-specific RAMS/lift plan for complex lifts.</li>
                  </ul>
                </td>
                <td className="py-3 px-4">Low</td>
              </tr>
              {/* LPG Presence / Transfer / Exchange */}
              <tr>
                <td className="py-3 px-4 font-bold">Residual LPG / Gas Transfer/Exchange</td>
                <td className="py-3 px-4">Fire, explosion, asphyxiation, cold burns, environmental release.</td>
                <td className="py-3 px-4">
                  <ul>
                    <li>Only Gas Safe engineers qualified to handle LPG.</li>
                    <li>Vessels certified to carry LPG, sited per UK Codes of Practice/HSE.</li>
                    <li>Strict procedures for liquid/vapour transfer (PCU use, manifold/flare stack if applicable on-site).</li>
                    <li>Leak detection procedures and equipment.</li>
                    <li>Adequate ventilation.</li>
                    <li>Risk assessment completed prior to any work involving gas escape.</li>
                    <li>Dry powder fire extinguishers readily available.</li>
                  </ul>
                </td>
                <td className="py-3 px-4">Low</td>
              </tr>
              {/* Installation / Removal of Vessels */}
              <tr>
                <td className="py-3 px-4 font-bold">Installation / Removal of Vessels</td>
                <td className="py-3 px-4">Crushing, striking, falls (from height), manual handling injuries, damage to property/utilities.</td>
                <td className="py-3 px-4">
                  <ul>
                    <li>Use of appropriate lifting equipment (crane).</li>
                    <li>Secure placement/chocking of vessels.</li>
                    <li>Clearance from structures/utilities.</li>
                    <li>Site-specific RAMS to identify ground conditions, underground services, access/egress.</li>
                    <li>Minimum of 2 people for operations.</li>
                    <li>Careful handling to prevent damage to valves/vessel.</li>
                  </ul>
                </td>
                <td className="py-3 px-4">Low</td>
              </tr>
              {/* Valve Work (Removal/Replacement) */}
              <tr>
                <td className="py-3 px-4 font-bold">Existing Valve Removal / Replacement</td>
                <td className="py-3 px-4">LPG release, fire, explosion, injury from residual pressure, manual handling.</td>
                <td className="py-3 px-4">
                  <ul>
                    <li>Examiner confirms vessel is free from liquid/vapour pressure before removal.</li>
                    <li>Use of correct tools and torque settings.</li>
                    <li>Jointing compounds and PTFE tape for sealing.</li>
                    <li>Tightness test after replacement.</li>
                    <li>Gas Safe engineers only.</li>
                  </ul>
                </td>
                <td className="py-3 px-4">Low</td>
              </tr>
              {/* Stud Fixing Movement/Replacement (if applicable on-site) */}
              <tr>
                <td className="py-3 px-4 font-bold">Stud Fixing Movement/Replacement (On-Site)</td>
                <td className="py-3 px-4">Burns, fire, explosion (if gas present), debris ingress, structural damage to vessel.</td>
                <td className="py-3 px-4">
                  <ul>
                    <li>Only commence after vessel degassed, purged, vented (minimum 48 hours).</li>
                    <li>All vessel openings plugged.</li>
                    <li>Careful paint removal (no gouging).</li>
                    <li>Gentle heating to remove moisture.</li>
                    <li>Correct stud welding procedure.</li>
                    <li>Appropriate PPE (welding mask, gloves).</li>
                  </ul>
                </td>
                <td className="py-3 px-4">Low</td>
              </tr>
              {/* General Site Hazards */}
              <tr>
                <td className="py-3 px-4 font-bold">General Site Hazards (Client Site)</td>
                <td className="py-3 px-4">Slips, trips, falls, collision with vehicles/plant, public interference, environmental contamination.</td>
                <td className="py-3 px-4">
                  <ul>
                    <li>Site-specific RAMS/briefing.</li>
                    <li>Request client site H&S policy guidelines.</li>
                    <li>Display suitable safety signage.</li>
                    <li>Constant monitoring of site environment.</li>
                    <li>Secure work area (barriers, cones).</li>
                    <li>Good housekeeping.</li>
                    <li>PPE (Hi-vis, appropriate footwear, hard hat).</li>
                  </ul>
                </td>
                <td className="py-3 px-4">Low</td>
              </tr>
              {/* Manual Handling */}
              <tr>
                <td className="py-3 px-4 font-bold">Manual Handling</td>
                <td className="py-3 px-4">Back injuries, strains, sprains.</td>
                <td className="py-3 px-4">
                  <ul>
                    <li>Minimise/avoid manual handling where possible (use mechanical aids).</li>
                    <li>Training on correct lifting techniques.</li>
                    <li>Assess load before lifting.</li>
                    <li>Team lifts for heavy/awkward items.</li>
                  </ul>
                </td>
                <td className="py-3 px-4">Low</td>
              </tr>
              {/* Lack of Competence/Training */}
              <tr>
                <td className="py-3 px-4 font-bold">Lack of Competence/Training</td>
                <td className="py-3 px-4">Incorrect procedures, accidents, equipment damage, non-compliance.</td>
                <td className="py-3 px-4">
                  <ul>
                    <li>All operations by qualified/competent personnel only.</li>
                    <li>Specific training for PCU use, lifting, gas handling, etc.</li>
                    <li>Written authorisation for PCU operators.</li>
                    <li>Regular reviews of competence.</li>
                  </ul>
                </td>
                <td className="py-3 px-4">Low</td>
              </tr>
              {/* Emergency Situation */}
              <tr>
                <td className="py-3 px-4 font-bold">Emergency Situation</td>
                <td className="py-3 px-4">Injury, fatality, delayed response, inadequate evacuation, increased hazard.</td>
                <td className="py-3 px-4">
                  <ul>
                    <li>Emergency stop buttons on equipment.</li>
                    <li>Immediate evacuation and notification of lead engineers/management.</li>
                    <li>Familiarise with fire alarm, fire exits, and alternative escape routes (site-specific).</li>
                    <li>Comply with fire wardens' instructions.</li>
                    <li>Promptly report any accident.</li>
                    <li>Dry powder fire extinguishers within easy reach.</li>
                    <li>First aid kit available.</li>
                    <li>Knowledge of client site emergency procedures.</li>
                  </ul>
                </td>
                <td className="py-3 px-4">Low</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      <section className="mb-8">
        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">5. Personal Protective Equipment (PPE)</h3>
        <p className="text-pvs-text-muted mb-2">Appropriate PPE shall be worn at ALL times when working on-site. This includes, but is not limited to:</p>
        <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
          <li>Hi-visibility Jacket/Vest</li>
          <li>Appropriate footwear (Rubber soled, Steel Toe-capped)</li>
          <li>Hard Hat</li>
          <li>Gloves (suitable for the task, e.g., chemical-resistant, heavy-duty for lifting)</li>
          <li>Eye protection (safety glasses/goggles, welding mask if stud work on-site)</li>
          <li>Respiratory Protective Equipment (RPE) if airborne contaminants or residual fumes are present.</li>
          <li>Hearing protection (ear defenders/plugs) during noisy operations.</li>
          <li>Photographic ID (required at all times on client sites).</li>
        </ul>
      </section>
      <section className="mb-8">
        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">6. Training and Competence</h3>
        <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
          <li>All on-site operations shall only be carried out by qualified personnel who have received appropriate training.</li>
          <li>Training will include:
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Safe operation of lorry-mounted cranes (if applicable to employee role).</li>
              <li>Safe operation of Portable Compressor Units (PCU), including written authorisation levels and limitations.</li>
              <li>Safe procedures for LPG liquid and vapour transfer/exchange.</li>
              <li>Valve removal, installation, and replacement procedures.</li>
              <li>Stud fixing and welding techniques (if applicable on-site).</li>
              <li>Pressure testing and tightness testing procedures.</li>
              <li>Operation and interpretation of ultrasonic examination equipment (if applicable on-site).</li>
              <li>Manual handling techniques.</li>
              <li>Emergency procedures, including fire, first aid, and specific LPG release protocols.</li>
              <li>Use and maintenance of all required Personal Protective Equipment (PPE).</li>
              <li>COSHH awareness, particularly for LPG and any other chemicals used on-site.</li>
              <li>Site-specific health and safety briefings.</li>
            </ul>
          </li>
          <li>Competence depends on a combination of training, experience, and formal assessment.</li>
        </ul>
      </section>
      <section className="mb-8">
        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">7. Emergency Procedures (On-Site)</h3>
        <p className="text-pvs-text-muted mb-2">In addition to general company emergency procedures, employees must familiarise themselves with client site-specific emergency plans.</p>
        <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
          <li><strong>Site-Specific Briefing:</strong> Prior to commencing work, all staff members present will be briefed on the scope of works and risks involved by the on-duty Senior Engineer, including site-specific emergency procedures.</li>
          <li><strong>Fire/Explosion (LPG):</strong> In the event of fire or uncontrolled LPG release, immediately activate site emergency alarms, evacuate the vicinity to a safe assembly point, and notify emergency services (999) and company management. Only attempt to tackle fire if trained and competent with appropriate extinguishers, and only if safe to do so.</li>
          <li><strong>Accidents/Injuries:</strong> Promptly report any accident at work involving personal injury, however trivial, to the Health and Safety Officer and client site contact. Record details in the Accident Book. Cooperate with any associated investigation. Familiarise with first aid facilities and trained first aiders on site. In a serious accident, dial 999 for emergency services, giving name, location, and brief details.</li>
          <li><strong>Equipment Malfunction:</strong> Immediately cease operations, activate emergency stops if applicable, and notify the on-duty Senior Engineer/management.</li>
          <li><strong>Road Accident (during transport):</strong> Follow standard road accident procedures, ensuring the safety of personnel, securing the scene, and contacting emergency services and company management as appropriate.</li>
        </ul>
      </section>
      <section className="mb-8">
        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">8. Environmental Considerations (On-Site)</h3>
        <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
          <li>Ensure proper containment and disposal protocols are in place for any potential spills of LPG or other residual substances.</li>
          <li>Minimize noise and emissions from vehicles and equipment.</li>
          <li>Ensure any waste generated on site is managed and disposed of in accordance with client site rules and environmental regulations.</li>
        </ul>
      </section>
      <section>
        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">9. Review</h3>
        <p className="text-pvs-text-muted">
          This Risk Assessment will be reviewed at regular intervals to ensure that it is achieving its aims effectively. It will be reviewed annually by the Board of Directors and amended appropriately with careful consideration and consultation with all relevant parties. It may also be reviewed following any incident, near miss, significant change in on-site operations, equipment, or client site conditions.
        </p>
      </section>
      
      {/* Employee Acknowledgment Section */}
      <EmployeeAcknowledgment 
        documentName="On Site RA"
        documentReference=""
        processActivity="On-Site LPG Vessel Operations"
        versionNumber=""
        dateOfIssue=""
      />
      </div>
    </div>
  </PVSPageLayout>
);

export default OnSiteRA;
